if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.dismissedUntilKey = 'spicevillage:newsletter-popup:dismissed-until';
        this.subscribedKey = 'spicevillage:newsletter-popup:subscribed';
        this.testMode = new URLSearchParams(window.location.search).has('newsletter_popup_test');
        this.testSuccess = new URLSearchParams(window.location.search).has('newsletter_popup_success');
        this.form = this.querySelector('[data-newsletter-popup-form]');
        this.form?.addEventListener('submit', this.handleSubmit.bind(this), {
          signal: this.abortController.signal,
        });

        window.FoxTheme.DOMready(this.handleOpen.bind(this));
      }

      prepareToHide() {
        super.prepareToHide();
        if (!this.signupComplete && !this.testMode) {
          window.SVTracking?.capture('storefront_newsletter_popup_dismissed');
          window.localStorage.setItem(this.dismissedUntilKey, String(Date.now() + 48 * 60 * 60 * 1000));
        }
      }

      handleOpen() {
        if (Shopify.designMode) return;

        if (!this.testMode && window.localStorage.getItem(this.subscribedKey) === 'true') {
          return false;
        }

        const dismissedUntil = Number(window.localStorage.getItem(this.dismissedUntilKey) || 0);
        if (!this.testMode && dismissedUntil > Date.now()) {
          return false;
        }

        if (this.testMode) {
          this.show();
          if (this.testSuccess) this.showSuccess();
          return;
        }

        switch (this.triggerOpen) {
          case 'delay':
            setTimeout(() => {
              this.show();
              window.SVTracking?.capture('storefront_newsletter_popup_shown');
            }, 5000);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(700, () => {
              this.show();
              window.SVTracking?.capture('storefront_newsletter_popup_shown');
            });
            break;
          default:
            this.show();
            window.SVTracking?.capture('storefront_newsletter_popup_shown');
            break;
        }
      }

      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        window.addEventListener('scroll', (e) => {
          let scrollTop =
            window.pageYOffset || (document.documentElement || document.body.parentNode || document.body).scrollTop;

          if (scrollTop - start > offset && !opened) {
            trigger();
            opened = true;
            return;
          }
        });
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (!this.form || this.form.dataset.loading === 'true') return;

        const email = this.form.elements.email.value.trim();
        window.SVTracking?.capture('storefront_newsletter_signup_attempted', { surface: 'popup' });
        const website = this.form.elements.website.value;
        const submitButton = this.form.querySelector('[data-newsletter-popup-submit]');
        const errorMessage = this.form.querySelector('[data-newsletter-popup-error]');
        const successMessage = this.querySelector('[data-newsletter-popup-success-message]');

        errorMessage.classList.add('hidden');
        this.form.dataset.loading = 'true';
        submitButton?.setAttribute('disabled', '');

        try {
          const response = await fetch(this.form.dataset.endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Newsletter-Key': this.form.dataset.formKey,
            },
            body: JSON.stringify({ email, website }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.detail || 'We could not complete the signup. Please try again.');
          }

          this.signupComplete = true;
          window.localStorage.setItem(this.subscribedKey, 'true');
          window.localStorage.removeItem(this.dismissedUntilKey);
          successMessage.textContent = data.message;
          this.showSuccess();
          document.dispatchEvent(new CustomEvent('newsletter:signup-completed'));
        } catch (error) {
          window.SVTracking?.capture('storefront_newsletter_signup_failed', { surface: 'popup' });
          errorMessage.textContent = error.message;
          errorMessage.classList.remove('hidden');
        } finally {
          this.form.dataset.loading = 'false';
          submitButton?.removeAttribute('disabled');
        }
      }

      showSuccess() {
        const signup = this.querySelector('[data-newsletter-popup-signup]');
        const success = this.querySelector('[data-newsletter-popup-success]');
        signup?.classList.add('hidden');
        success?.classList.remove('hidden');
        success?.focus();
      }
    }
  );
}
